import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { handleKeyboardActivation } from '@/utils/a11yUtils';

const SectionHeader = ({ 
  title, 
  sectionId, 
  viewAllPath, 
  viewAllLabel = "View All", 
  viewAllColor = "sky", 
  showCreateButton = false, 
  onCreateClick,
  sectionOrder,
  onMoveSectionUp,
  onMoveSectionDown
}) => {
  const navigate = useNavigate();
  const currentIndex = sectionOrder.indexOf(sectionId);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === sectionOrder.length - 1;

  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
      <div className="flex items-center gap-2 sm:gap-3">
        <h2 className="text-lg sm:text-2xl font-semibold">{title}</h2>
        <div className="flex gap-1">
          <button
            onClick={() => onMoveSectionUp(sectionId)}
            onKeyDown={(e) => handleKeyboardActivation(e, () => onMoveSectionUp(sectionId))}
            disabled={isFirst}
            aria-label={`Move ${title} section up`}
            className={`p-2 rounded hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 min-h-[44px] min-w-[44px] flex items-center justify-center ${isFirst ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
            title="Move section up"
          >
            <ChevronUp className="w-4 h-4 text-gray-600" aria-hidden="true" />
          </button>
          <button
            onClick={() => onMoveSectionDown(sectionId)}
            onKeyDown={(e) => handleKeyboardActivation(e, () => onMoveSectionDown(sectionId))}
            disabled={isLast}
            aria-label={`Move ${title} section down`}
            className={`p-2 rounded hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 min-h-[44px] min-w-[44px] flex items-center justify-center ${isLast ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
            title="Move section down"
          >
            <ChevronDown className="w-4 h-4 text-gray-600" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        {viewAllPath && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate(viewAllPath)}
            className={`border-${viewAllColor}-200 hover:bg-${viewAllColor}-50 text-${viewAllColor}-600 text-xs sm:text-sm min-h-[44px] px-2 sm:px-3`}
          >
            <span className="hidden sm:inline">{viewAllLabel}</span>
            <span className="sm:hidden">View</span>
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
        {showCreateButton && (
          <Button
            onClick={onCreateClick}
            data-testid="create-new-resume-btn"
            className="bg-sky-600 hover:bg-sky-700 text-white text-xs sm:text-sm min-h-[44px] px-2 sm:px-3"
          >
            <span className="hidden sm:inline">+ Create New</span>
            <span className="sm:hidden">+</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default SectionHeader;
